import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card, Col, Layout, Row, Typography } from "antd";
import {
  CheckCircleOutlined,
  FilePdfOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import CustomHeader from "../Header";
import Footer from "../Footer";
import "./PaymentFeature.css";
import {
  paymentAdvantages,
  paymentDashboardFeatures,
  paymentFeaturedCards,
  paymentFeatureText,
  paymentTargetAudience,
  paymentTemplates,
} from "./paymentFeatureConstant";
import PaymentCardScroller from "./PaymentCardScroller";
import { scrollToElementById } from "../../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

const PaymentFeature: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("payment");
  }, [pathname]);

  return (
    <div id="payment">
      <Helmet>
        <title>Payment Management Software for Businesses | Ease Margin</title>
        <meta
          name="description"
          content="Simplify your business payments with EaseMargin. Payment Management Software lets you track, manage, and control all transactions efficiently in one platform."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/payment-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="paymentFeature-layout">
        <Header className="paymentFeature-header">
          <div className="paymentFeature-header-inner">
            <Title level={1} className="paymentFeature-title-large">
              {paymentFeatureText.HERO_TITLE}
            </Title>
            <Paragraph className="paymentFeature-paragraph-header">
              {paymentFeatureText.HERO_DESCRIPTION.split("\n\n").map(
                (para, idx) => (
                  <p key={idx} style={{ marginBottom: "1em" }}>
                    {para}
                  </p>
                )
              )}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>
          </div>
        </Header>
        <Content className="paymentFeature-content">
          <Title level={2} className="paymentFeature-title-2nd">
            {paymentFeatureText.INTRO_TITLE}
          </Title>
          <Paragraph className="paymentFeature-paragraph-main">
            {paymentFeatureText.INTRO_DESCRIPTION}
          </Paragraph>
          <Title level={3} className="paymentFeature-title-3rd">
            {paymentFeatureText.SALES_FLOW_TITLE}
          </Title>
          <PaymentCardScroller />
          <Title level={3} className="paymentFeature-title-3rd-marginTop">
            {paymentFeatureText.MULTI_FORMAT_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginTop: 20 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {paymentFeatureText.DOC_FORMAT}
                </Title>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>💵 Cash</Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>🧾 Cheque</Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🏦 Bank Transfer
                    </Paragraph>
                  </Col>{" "}
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      📲 UPI (manual entry only)
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {paymentFeatureText.SHARING_STORE}
                </Title>
                <Row gutter={[0, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#f5222d" }}
                      />
                      Download payment records
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Print payment summaries
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <SendOutlined
                        style={{ marginRight: 8, color: "#1890ff" }}
                      />
                      Send via Email
                    </Paragraph>
                  </Col>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <WhatsAppOutlined
                        style={{ marginRight: 8, color: "#25D366" }}
                      />
                      Send via WhatsApp
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {paymentTemplates.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="paymentFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="paymentFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="paymentFeature-title-3rd-marginTop">
            {paymentFeatureText.DASHBOARD_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
            {paymentDashboardFeatures.map((feature, index) => (
              <Col xs={24} sm={12} key={index}>
                <Card
                  bordered={false}
                  className="card-default"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="paymentFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {feature}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {paymentFeaturedCards.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="paymentFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="paymentFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Title level={3} className="paymentFeature-title-3rd-marginTop">
            {paymentFeatureText.ADVANTAGE_TITLE}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
            {paymentAdvantages.map((advantage, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="paymentFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {advantage}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="paymentFeature-title-3rd-marginTop">
            {paymentFeatureText.PERFECT_FOR_TITLE}
          </Title>
          <Row gutter={[16, 16]}>
            {paymentTargetAudience.map((target, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#FFF1F0" }}
                >
                  <Paragraph className="paymentFeature-paragraph-targetAudience">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {target}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Card
            bordered={false}
            className="card-default paymentFeature-card-footer"
          >
            <Title level={2} className="paymentFeature-title-footer">
              {paymentFeatureText.CTA_TITLE}
            </Title>
            <Paragraph className="paymentFeature-paragraph-footer">
              {paymentFeatureText.CTA_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                {paymentFeatureText.CTA_BUTTON}
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default PaymentFeature;
